<template>
  <div class="trainers-list">
    <div class="trainers-header">
      <h3 class="trainers-title">Персональные тренеры</h3>
      <div class="scroll-buttons">
        <button
          class="nav-button"
          @click="scrollLeft"
        >
          <img :src="arrowLeftIcon" alt="Previous" />
        </button>

        <button
          class="nav-button"
          @click="scrollRight"
        >
          <img :src="arrowRightIcon" alt="Next" />
        </button>
      </div>
    </div>

    <div class="trainers-container" ref="containerRef">
      <button
        v-for="trainer in trainers"
        :key="trainer.id"
        class="trainer-card"
        :class="{ active: isSelected(trainer.id) }"
        @click="toggleTrainer(trainer.id)"
      >
        <div v-if="trainer.avatar" class="trainer-avatar-wrapper">
          <img :src="trainer.avatar" :alt="trainer.firstName" class="trainer-avatar" />
          <div class="trainer-rating">
            <img :src="starIcon" alt="" class="star-icon" />
            <span>{{ trainer.rating.toFixed(1) }}</span>
          </div>
        </div>
        <div v-else class="trainer-rating trainer-rating--standalone">
          <img :src="starIcon" alt="" class="star-icon" />
          <span>{{ trainer.rating.toFixed(1) }}</span>
        </div>
        <div class="trainer-info">
          <div class="trainer-name">{{ trainer.firstName }}</div>
          <div class="trainer-surname">{{ trainer.lastName }}</div>
        </div>
        <div class="trainer-price">от {{ formatPrice(trainer.minPrice, trainer.currency) }}</div>
      </button>
    </div>
  </div>
</template>

<script setup>
/* global defineProps, defineEmits */
import { ref } from 'vue'
import { formatCurrencyPrice, pickPreferredCurrency } from '@/widgets/booking/utils/currency'
import starIcon from '@/widgets/booking/assets/img/icons/booking-star.svg'
import arrowLeftIcon from "@/widgets/booking/assets/img/icons/arrow_left.svg";
import arrowRightIcon from "@/widgets/booking/assets/img/icons/arrow_right.svg";

const props = defineProps({
  trainers: {
    type: Array,
    required: true
  },
  currency: {
    type: String,
    default: 'RUB'
  },
  modelValue: {
    type: Number,
    default: undefined
  }
})

const emit = defineEmits(['update:modelValue'])

const containerRef = ref(null)

const isSelected = (trainerId) => {
  return props.modelValue === trainerId
}

const toggleTrainer = (trainerId) => {
  if (props.modelValue === trainerId) {
    emit('update:modelValue', undefined)
  } else {
    emit('update:modelValue', trainerId)
  }
}

const formatPrice = (price, currency = props.currency) => {
  return formatCurrencyPrice(price, pickPreferredCurrency(currency, props.currency))
}

const scrollLeft = () => {
  if (containerRef.value) {
    containerRef.value.scrollBy({ left: -300, behavior: 'smooth' })
  }
}

const scrollRight = () => {
  if (containerRef.value) {
    containerRef.value.scrollBy({ left: 300, behavior: 'smooth' })
  }
}
</script>

<style scoped lang="scss">
@import '@/widgets/booking/assets/scss/variables.scss';

.trainers-list {
  background: white;
  border-radius: 12px;
  margin-bottom: $spacing-lg;
}

.trainers-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.trainers-title {
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 17px;
  font-weight: 700;
  line-height: 20px;
  color: #252525;
  margin: 0;
}

.scroll-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.nav-button {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #F3F5F8;
  border-radius: 8px;
  color: var(--booking-widget-primary-color, #3C67D6);
  transition: all 0.2s ease;
  flex-shrink: 0;

  img {
    width: 12px;
    height: 12px;
  }

  &:hover {
    background: darken(#F3F5F8, 5%);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 20px;
    height: 20px;
  }
}

.trainers-container {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 4px 0;

  &::-webkit-scrollbar {
    display: none;
  }
}

.trainer-card {
  flex-shrink: 0;
  width: 125px;
  height: 160px;
  padding: 12px;
  background: #F3F5F8;
  border: 1px solid transparent;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: darken(#F3F5F8, 3%);
  }

  &.active {
    background: var(--booking-widget-primary-color-soft, #3C67D61A);
    border-color: var(--booking-widget-primary-color, #3C67D6);
  }
}

.trainer-avatar-wrapper {
  position: relative;
  flex-shrink: 0;
}

.trainer-avatar {
  width: 64px;
  height: 64px;
  border-radius: 48px;
  object-fit: cover;
  display: block;
}

.trainer-rating {
  position: absolute;
  bottom: -10px;
  left: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 4px 8px;
  background: #FFFFFF;
  border-radius: 12px;
  width: 50px;
  height: 24px;

  span {
    font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    text-align: center;
    color: #333333;
  }
}

.trainer-rating--standalone {
  position: static;
  margin-bottom: 4px;
}

.star-icon {
  width: 14px;
  height: 14px;
  display: block;
}

.trainer-info {
  text-align: left;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: 8px;
}

.trainer-name {
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 16px;
  color: #333333;
}

.trainer-surname {
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 16px;
  color: #333333;
}

.trainer-price {
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #757885;
  text-align: left;
}
</style>
